import React from 'react';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Player, SelectOption } from 'src/models';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import AutocompleteWrapper from 'src/components/Picks/AutocompleteWrapper';
import TextFieldWrapper from 'src/components/Picks/TextFieldWrapper';
import PlayersAutocomplete from 'src/components/Picks/PlayersAutocomplete';

const INITIAL_FORM_STATE = {
  score1: '',
  score2: '',
  homePlayer: { name: '', id: null },
  awayPlayer: { name: '', id: null },
  numOfGames: '',
  team: '',
};

const FORM_VALIDATION = Yup.object().shape({
  score1: Yup.number()
    .required('Required')
    .test('valid-score', 'Switch values', (value, context) =>
      value ? context.parent.score2 < value : false
    ),
  score2: Yup.number().required('Required'),
  homePlayer: Yup.object().shape({
    id: Yup.number().required(),
    fullName: Yup.string().required(),
  }),
  awayPlayer: Yup.object().shape({
    id: Yup.number().required(),
    fullName: Yup.string().required(),
  }),
  numOfGames: Yup.number().min(4).max(7).required('Required'),
  team: Yup.object().shape({
    id: Yup.number().required(),
    name: Yup.string().required(),
  }),
});

export type PickFormProps = {
  gamePk: number;
  playoffRound: number;
  playoffSeriesLetter: string;
  season: number;
  homeTeam: string;
  awayTeam: string;
  homePlayers: Player[];
  awayPlayers: Player[];
  teams: SelectOption[];
  stepIndex: number;
  numOfSteps: number;
  handleContinue: (data: any) => void;
  handleBack: () => void;
};

const PickForm = ({
  gamePk,
  playoffRound,
  playoffSeriesLetter,
  season,
  homeTeam,
  awayTeam,
  homePlayers,
  awayPlayers,
  teams,
  stepIndex,
  numOfSteps,
  handleContinue,
  handleBack,
}: PickFormProps): JSX.Element => {
  return (
    <Card>
      <CardHeader title={`${homeTeam} vs ${awayTeam}`} />
      <Divider />
      <CardContent>
        <Formik
          initialValues={{ ...INITIAL_FORM_STATE }}
          validationSchema={FORM_VALIDATION}
          onSubmit={(data) => {
            handleContinue({
              gamePk,
              playoffRound,
              playoffSeriesLetter,
              season,
              ...data,
            });
          }}
        >
          <Form>
            <Grid container spacing={2}>
              <Grid item container xs={12} spacing={1}>
                <Grid item>
                  <Typography>Score</Typography>
                </Grid>
                <Grid item>
                  <Tooltip title='Left number must be greater than right number, eg. 4-2'>
                    <InfoIcon fontSize='small' />
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item container spacing={1} justifyContent='flex-start'>
                <Grid item xs={3} md={2}>
                  <TextFieldWrapper name='score1' min={0} max={10} />
                </Grid>
                <Grid item xs={3} md={2}>
                  <TextFieldWrapper name='score2' min={0} max={10} />
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <Typography>Game Winner</Typography>
              </Grid>
              <Grid item xs={12}>
                <PlayersAutocomplete
                  id='homePlayer'
                  options={homePlayers}
                  label={`${homeTeam} Player`}
                />
              </Grid>
              <Grid item xs={12}>
                <PlayersAutocomplete
                  id='awayPlayer'
                  options={awayPlayers}
                  label={`${awayTeam} Player`}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography>Number of Games</Typography>
              </Grid>
              <Grid item xs={3}>
                <TextFieldWrapper name='numOfGames' min={4} max={7} />
              </Grid>
              <Grid item xs={12}>
                <Typography>Series Winner</Typography>
              </Grid>
              <Grid item xs={12}>
                <AutocompleteWrapper id='team' options={teams} label='Team' />
              </Grid>
              <Grid item xs={6}>
                <Button variant='contained' fullWidth={true} type='submit'>
                  {stepIndex === numOfSteps - 1 ? 'Review' : 'Continue'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  disabled={stepIndex === 0}
                  variant='outlined'
                  fullWidth={true}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </CardContent>
    </Card>
  );
};

export default PickForm;
